/**
 * The Today screen. Section 7.
 *
 * The screen used 90% of the time: day navigation, the day's session with per-set tick
 * targets and a working weight per exercise, the eating slots with tick and swap, the
 * three daily habits, banners for hold and de-load weeks, and calorie/protein meters
 * that count only what has been ticked.
 *
 * Every tick target reports observed state. Nothing here is optimistic.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Tick, TickBar, Meter, Ring, Banner } from './components.tsx';
import { VideoLink } from './VideoLink.tsx';
import { LogMeal } from './LogMeal.tsx';
import type { TickState } from './components.tsx';
import { PLAN, META } from '../plan/data.ts';
import { positionFor, dateForDayIndex, MAX_DAY_INDEX } from '../plan/dates.ts';
import { phaseFor, isDeload, gateForWeek } from '../plan/phase.ts';
import {
  kindOf, liftSessionFor, runSessionFor, recoverySessionFor,
} from '../plan/session.ts';
import {
  effectiveSessionId, planShift, isOverridden, weekHasOverrides, weekResetKeys,
  scheduleWarnings, missedCandidates, LABELS,
} from '../plan/reschedule.ts';
import type { SessionId } from '../plan/session.ts';
import {
  slotsFor, mealFor, mealIndexFor, nextSwapIndex, dayPlan, eatenSoFar,
  dayPlanBaseline, isCustom, customCountFor,
} from '../plan/nutrition.ts';
import { dayProgress, sessionProgress, HABITS } from '../plan/progress.ts';
import type { SlotName } from '../plan/types.ts';

const HABIT_LABELS: Record<string, string> = {
  water: 'Water', steps: '9,000 steps', vitd: 'Vitamin D',
};
const DOW_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SLOT_LABELS: Record<SlotName, string> = {
  breakfast: 'Breakfast', am: '10:30 snack', lunch: 'Lunch',
  pm: '16:00 snack', dinner: 'Dinner', post: 'Post-training',
};

export function Today({ api }: { api: StoreApi }) {
  const { state, durable } = api;
  const startDate = state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const todayPos = useMemo(() => positionFor(startDate, new Date()), [startDate]);
  const [dayIndex, setDayIndex] = useState(todayPos.dayIndex);
  const [logging, setLogging] = useState<SlotName | null>(null);

  const week = Math.floor(dayIndex / 7) + 1;
  const dow = dayIndex % 7;
  const sessionEntry = effectiveSessionId(state, dayIndex);
  const moved = isOverridden(state, dayIndex);
  const phase = phaseFor(state, startDate, week);
  const date = dateForDayIndex(startDate, dayIndex);

  const plan = dayPlan(state, dayIndex);
  const eaten = eatenSoFar(state, dayIndex);
  const progress = dayProgress(state, startDate, dayIndex);
  const sets = sessionEntry === 'rest'
    ? { done: 0, total: 0 }
    : sessionProgress(state, startDate, week, dayIndex, sessionEntry);
  const standingWarnings = scheduleWarnings(state, week);

  const tickState = (key: string, on: boolean): TickState =>
    api.pending.has(key) ? 'writing' : api.errorFor(key) ? 'failed' : on ? 'on' : 'off';

  if (todayPos.complete) return <PlanComplete api={api} startDate={startDate} />;

  return (
    <>
      <DayNav
        dayIndex={dayIndex} date={date} week={week} dow={dow}
        todayIndex={todayPos.dayIndex}
        onStep={(delta) => setDayIndex((d) => Math.min(MAX_DAY_INDEX, Math.max(0, d + delta)))}
        onGoTo={setDayIndex}
      />

      {todayPos.notStarted && dayIndex === todayPos.dayIndex && (
        <Banner kind="info" title="The plan has not started yet">
          Day 1 is {dateForDayIndex(startDate, 0)}. You are looking at it {-todayPos.rawDay}
          {-todayPos.rawDay === 1 ? ' day' : ' days'} early. Ticks still save.
        </Banner>
      )}
      {phase.hold && !phase.forced && (
        <Banner kind="warn" title={`Held at level ${phase.eff}`}>
          Checkpoint {phase.unlocked} is not met, so this week serves the last build week of
          block {phase.eff} rather than moving on. A hold lasts at most two weeks.
        </Banner>
      )}
      {phase.forced && (
        <Banner kind="warn" title={`Level ${phase.eff} has opened anyway`}>
          You have been held {phase.held} weeks, which is the limit. The level opens regardless
          — but set <strong>every working weight 10% lower</strong> than you otherwise would.
        </Banner>
      )}
      {isDeload(week) && !phase.hold && (
        <Banner kind="info" title={`Week ${week} is a de-load`}>
          One set fewer per exercise, at the same weight. The test sits at the end of this week.
          {gateForWeek(week) && <> Checkpoint: <strong>{gateForWeek(week)!.name}</strong>.</>}
        </Banner>
      )}

      <div className="daysummary card">
        <Ring done={progress.done} total={progress.total} />
        <div className="daysummary-meters">
          <Meter label="Calories ticked" done={eaten.k} total={plan.k} unit="kcal" />
          <Meter label="Protein ticked" done={eaten.p} total={plan.p} unit="g" />
        </div>
      </div>

      {customCountFor(state, dayIndex) > 0 && (() => {
        const base = dayPlanBaseline(state, dayIndex);
        const dk = Math.round(plan.k - base.k);
        const dp = Math.round(plan.p - base.p);
        const n = customCountFor(state, dayIndex);
        return (
          <Banner kind="info" title={`${n} meal${n === 1 ? '' : 's'} logged instead of the plan`}>
            Today now comes to {Math.round(plan.k)} kcal and {Math.round(plan.p)} g protein.
            The plan had {Math.round(base.k)} kcal and {Math.round(base.p)} g, so you are{' '}
            {dk === 0 ? 'level on calories' : `${dk > 0 ? dk + ' over' : Math.abs(dk) + ' under'} on calories`}
            {' and '}
            {dp === 0 ? 'level on protein' : `${dp > 0 ? dp + ' g over' : Math.abs(dp) + ' g under'} on protein`}.
          </Banner>
        );
      })()}

      {sessionEntry === 'rest' ? (
        <section>
          <h2>Rest — session missed</h2>
          <div className="card">
            <p className="sub" style={{ margin: 0 }}>
              Nothing scheduled here. This day was moved out of the way when you picked the
              week up again, so it is on the lighter-day intake: no post-training meal.
            </p>
          </div>
        </section>
      ) : (
        <SessionCard
          api={api} sessionId={sessionEntry} dayIndex={dayIndex} week={week}
          startDate={startDate} phase={phase.eff} sets={sets} tickState={tickState}
        />
      )}

      <Reschedule api={api} dayIndex={dayIndex} week={week} startDate={startDate}
                  moved={moved} standingWarnings={standingWarnings} />

      <h2>Eating</h2>
      {slotsFor(state, dayIndex).map((slot) => {
        const meal = mealFor(state, dayIndex, slot);
        const key = `meal:${dayIndex}:${slot}`;
        const on = state.get(key) === 1;
        const swapKey = `swap:${dayIndex}:${slot}`;
        const logged = isCustom(state, dayIndex, slot);
        return (
          <div className="card meal" key={slot}>
            <TickBar
              state={tickState(key, on)}
              disabled={!durable}
              onToggle={() => void api.setFlag(key, !on)}
              title={<>
                <span className="slot">{SLOT_LABELS[slot]}</span> {meal.n}
                {logged && <span className="movedtag">logged</span>}
              </>}
              sub={logged ? 'You logged this yourself' : meal.d}
              right={<span className="macro">{meal.k} kcal<br />{meal.p} g P</span>}
            />
            <div className="meal-foot">
              <span className="dim">
                {logged
                  ? `${meal.f} g fat · ${meal.c} g carb`
                  : <>{meal.t} · {meal.f} g fat · {meal.c} g carb
                      {' · '}option {mealIndexFor(state, dayIndex, slot) + 1} of {PLAN.food[slot].length}</>}
              </span>
              <span className="btns" style={{ marginTop: 0 }}>
                {!logged && (
                  <button className="small" disabled={!durable || api.pending.has(swapKey)}
                          onClick={() => void api.put(swapKey, nextSwapIndex(state, dayIndex, slot))}>
                    {api.pending.has(swapKey) ? 'Swapping…' : 'Swap'}
                  </button>
                )}
                <button className="small" disabled={!durable}
                        onClick={() => setLogging(logging === slot ? null : slot)}>
                  {logging === slot ? 'Close' : logged ? 'Edit' : 'Ate something else'}
                </button>
              </span>
            </div>
            {logging === slot && (
              <LogMeal api={api} dayIndex={dayIndex} slot={slot} onClose={() => setLogging(null)} />
            )}
            {api.errorFor(key) && <ErrorLine msg={api.errorFor(key)!} />}
            {api.errorFor(swapKey) && <ErrorLine msg={api.errorFor(swapKey)!} />}
          </div>
        );
      })}

      <h2>Daily habits</h2>
      <div className="card">
        <p className="sub" style={{ marginTop: 0 }}>
          Tracked, but deliberately not counted in the ring above.
        </p>
        {HABITS.map((h) => {
          const key = `habit:${h}:${dayIndex}`;
          const on = state.get(key) === 1;
          return (
            <div key={h}>
              <TickBar
                state={tickState(key, on)} disabled={!durable}
                onToggle={() => void api.setFlag(key, !on)}
                title={HABIT_LABELS[h]}
              />
              {api.errorFor(key) && <ErrorLine msg={api.errorFor(key)!} />}
            </div>
          );
        })}
      </div>
    </>
  );
}

function ErrorLine({ msg }: { msg: string }) {
  return <div className="errline">Not saved — {msg}</div>;
}

function DayNav({ dayIndex, date, week, dow, todayIndex, onStep, onGoTo }: {
  dayIndex: number; date: string; week: number; dow: number; todayIndex: number;
  onStep: (delta: number) => void; onGoTo: (n: number) => void;
}) {
  // Stepping uses a functional update, so rapid taps each advance a day rather than
  // all writing the same value from a stale render.
  return (
    <div className="daynav">
      <button className="navbtn" aria-label="Previous day" disabled={dayIndex <= 0}
              onClick={() => onStep(-1)}>‹</button>
      <div className="daynav-mid">
        <div className="daynav-day">{DOW_NAMES[dow]}</div>
        <div className="daynav-sub">
          Week {week} · Day {dayIndex + 1} of 112 · {date}
          {dayIndex !== todayIndex && (
            <> · <button className="linkbtn" onClick={() => onGoTo(todayIndex)}>today</button></>
          )}
        </div>
      </div>
      <button className="navbtn" aria-label="Next day" disabled={dayIndex >= MAX_DAY_INDEX}
              onClick={() => onStep(1)}>›</button>
    </div>
  );
}

function SessionCard({ api, sessionId, dayIndex, week, startDate, phase, sets, tickState }: {
  api: StoreApi; sessionId: SessionId; dayIndex: number; week: number; startDate: string;
  phase: number; sets: { done: number; total: number };
  tickState: (key: string, on: boolean) => TickState;
}) {
  const { state, durable } = api;
  const kind = kindOf(sessionId);

  const header = (title: string, focus: string, extra?: string) => (
    <div className="sess-head">
      <div>
        <h2 style={{ margin: 0 }}>{title}</h2>
        {extra && <div className="sub" style={{ margin: '2px 0 0' }}>{extra}</div>}
      </div>
      <span className="sess-count">{sets.done}/{sets.total}</span>
      <p className="focus">{focus}</p>
    </div>
  );

  if (kind === 'lift') {
    const s = liftSessionFor(state, startDate, week, sessionId as 'liftA' | 'liftB');
    return (
      <section>
        {header(s.name, s.focus,
          `Block ${phase}${s.deloaded ? ' · de-load: one set fewer, same weight' : ''}`)}
        {s.ex.map((e, exIndex) => {
          const loadKey = `load:${phase}:${sessionId}:${exIndex}`;
          return (
            <div className="card ex" key={exIndex}>
              <div className="ex-head">
                <div className="ex-name">{e.n}</div>
                <div className="ex-reps">
                  {e.plannedSets} × {e.r}
                  {e.unilateral && <span className="dim"> ({e.plannedSets * 2} sets total)</span>}
                </div>
              </div>
              <p className="coach">{e.c}</p>
              <VideoLink api={api} ex={e} />
              <div className="ex-row">
                <div className="ticks">
                  {Array.from({ length: e.plannedSets }, (_, i) => {
                    const key = `set:${dayIndex}:${sessionId}:${exIndex}:${i}`;
                    const on = state.get(key) === 1;
                    return (
                      <Tick key={i} state={tickState(key, on)} disabled={!durable}
                            label={`${e.n} set ${i + 1}`}
                            onToggle={() => void api.setFlag(key, !on)} />
                    );
                  })}
                </div>
                <LoadField api={api} loadKey={loadKey} />
              </div>
              {Array.from({ length: e.plannedSets }, (_, i) =>
                `set:${dayIndex}:${sessionId}:${exIndex}:${i}`)
                .map((k) => api.errorFor(k) && <ErrorLine key={k} msg={api.errorFor(k)!} />)}
            </div>
          );
        })}
      </section>
    );
  }

  if (kind === 'run') {
    const s = runSessionFor(state, startDate, week, sessionId as 'run0');
    return (
      <section>
        {header(s.title, s.note,
          `${s.runningMinutes} min running${s.week !== week ? ` · serving week ${s.week}` : ''}`)}
        <div className="card">
          <div className="walk">{s.warmUp.n} — {s.warmUp.r}. <span className="dim">{s.warmUp.c}</span></div>
        </div>
        {s.blocks.map((b, blockIndex) => (
          <div className="card ex" key={blockIndex}>
            <div className="ex-head">
              <div className="ex-name">{b.n}</div>
              <div className="ex-reps">{b.s} × {b.min > 0 ? `${b.min} min` : 'walk'}</div>
            </div>
            {b.c && <p className="coach">{b.c}</p>}
            <div className="ticks">
              {Array.from({ length: b.s }, (_, i) => {
                const key = `set:${dayIndex}:${sessionId}:${blockIndex}:${i}`;
                const on = state.get(key) === 1;
                return (
                  <Tick key={i} state={tickState(key, on)} disabled={!durable}
                        label={`${b.n} rep ${i + 1}`}
                        onToggle={() => void api.setFlag(key, !on)} />
                );
              })}
            </div>
          </div>
        ))}
        <div className="card">
          <div className="walk">{s.coolDown.n} — {s.coolDown.r}. <span className="dim">{s.coolDown.c}</span></div>
        </div>
      </section>
    );
  }

  const s = recoverySessionFor(sessionId as 'recWed' | 'recSun');
  return (
    <section>
      {header(s.name, s.focus)}
      {s.ex.map((e, exIndex) => (
        <div className="card ex" key={exIndex}>
          <div className="ex-head">
            <div className="ex-name">{e.n}</div>
            <div className="ex-reps">{e.r}</div>
          </div>
          {e.c && <p className="coach">{e.c}</p>}
          <VideoLink api={api} ex={e} />
          <div className="ticks">
            {Array.from({ length: e.s }, (_, i) => {
              const key = `set:${dayIndex}:${sessionId}:${exIndex}:${i}`;
              const on = state.get(key) === 1;
              return (
                <Tick key={i} state={tickState(key, on)} disabled={!durable}
                      label={`${e.n} ${i + 1}`}
                      onToggle={() => void api.setFlag(key, !on)} />
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}

/**
 * Working weight. Keyed per PHASE, so entering it once on the Monday of week 5 pre-fills
 * weeks 6, 7 and 8 — which is what makes "add 2.5kg when you hit the top of the range"
 * usable at all.
 *
 * Committed on blur, on a short debounce, AND when the app is hidden. Blur alone is not
 * enough: if you type a weight and swipe the app away, iOS can terminate the process
 * before a blur ever fires, and the whole progressive-overload rule depends on these
 * surviving. Every commit is an upsert on one key, so the extra writes cost one row.
 */
function LoadField({ api, loadKey }: { api: StoreApi; loadKey: string }) {
  const stored = api.state.get<string>(loadKey) ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? stored;
  const pending = api.pending.has(loadKey);
  const err = api.errorFor(loadKey);

  const draftRef = useRef<string | null>(draft);
  draftRef.current = draft;
  const storedRef = useRef(stored);
  storedRef.current = stored;

  const commit = useCallback(async () => {
    const d = draftRef.current;
    if (d === null || d === storedRef.current) { setDraft(null); return; }
    const ok = d.trim() === '' ? await api.del(loadKey) : await api.put(loadKey, d.trim());
    if (ok) setDraft(null);
  }, [api, loadKey]);

  // Debounce: never leave a typed weight uncommitted for longer than this.
  useEffect(() => {
    if (draft === null || draft === stored) return;
    const t = setTimeout(() => { void commit(); }, 700);
    return () => clearTimeout(t);
  }, [draft, stored, commit]);

  // The swipe-away case.
  useEffect(() => {
    const flush = () => { if (document.visibilityState === 'hidden') void commit(); };
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, [commit]);

  return (
    <label className="load">
      <span className="load-lab">kg</span>
      <input
        type="text" inputMode="decimal" value={value} disabled={pending}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => { if (e.key === 'Enter') void commit(); }}
        aria-label="working weight in kilograms"
        className={err ? 'bad-input' : undefined}
      />
      {pending && <span className="load-state">saving…</span>}
      {!pending && draft !== null && draft !== stored && <span className="load-state">saving…</span>}
      {err && <span className="load-state bad">not saved</span>}
    </label>
  );
}

function PlanComplete({ api, startDate }: { api: StoreApi; startDate: string }) {
  const gate = PLAN.gates[3];
  return (
    <>
      <h1>Plan complete</h1>
      <p className="sub">16 weeks, {dateForDayIndex(startDate, 0)} to {dateForDayIndex(startDate, MAX_DAY_INDEX)}.</p>
      <Banner kind="ok" title="You reached the end of the block">
        The plan deliberately has a finish date rather than a rolling one. The last checkpoint
        asks you to book what comes next.
      </Banner>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>{gate.name} → {gate.opens}</h2>
        <p className="focus">{gate.why}</p>
        {gate.crit.map((c, i) => (
          <div className="row" key={i}>
            <span className="k">{c.t}</span>
            <span className="v">{c.auto !== undefined ? 'automatic'
              : api.state.get(`crit:4:${i}`) === 1 ? '✓' : '—'}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Moving training days.
 *
 * The card is always present and always explains itself, whether or not there is
 * anything to act on. The first version showed a disabled button with a footnote,
 * which read as "the feature is missing" — the user went looking for it and could not
 * find it, which is the only test that matters.
 *
 * A uniform shift preserves relative spacing, which is why Lift A stays the same number
 * of days clear of the key run. Nothing here is silent: the preview names every day
 * that changes, everything that falls off the end of the week, and any rule the result
 * puts under strain. It warns rather than blocks.
 */
function Reschedule({ api, dayIndex, week, startDate, moved, standingWarnings }: {
  api: StoreApi; dayIndex: number; week: number; startDate: string;
  moved: boolean; standingWarnings: string[];
}) {
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const candidates = missedCandidates(api.state, dayIndex);
  const hasOverrides = weekHasOverrides(api.state, week);
  const movedCount = countMoved(api, week);
  const shift = chosen === null ? null : planShift(api.state, dayIndex, chosen);

  const close = () => { setPicking(false); setChosen(null); };

  const applyShift = async () => {
    if (!shift) return;
    setBusy(true);
    try { for (const w of shift.writes) await api.put(w.key, w.value); close(); }
    finally { setBusy(false); }
  };
  const resetWeek = async () => {
    if (!confirm(`Put week ${week} back on the plan? Any sessions you moved go back to their `
      + `original days. Ticks are not deleted — they stay attached to the session they were `
      + `made on, and reappear.`)) return;
    setBusy(true);
    try { for (const k of weekResetKeys(week)) if (api.state.has(k)) await api.del(k); }
    finally { setBusy(false); }
  };

  const dayLabel = (d: number) =>
    `${DOW_NAMES[d % 7].slice(0, 3)} ${dateForDayIndex(startDate, d).slice(5)}`;

  return (
    <>
      <h2>Schedule</h2>
      <div className="card">
        <div className="row">
          <span className="k">Week {week}</span>
          <span className="v">
            {movedCount === 0
              ? <span className="ok">following the plan</span>
              : <span className="warn">{movedCount} day{movedCount === 1 ? '' : 's'} moved</span>}
          </span>
        </div>
        {moved && (
          <div className="row">
            <span className="k">This day</span>
            <span className="v">moved from the original plan</span>
          </div>
        )}
        {standingWarnings.map((w) => <Banner kind="warn" key={w}>{w}</Banner>)}

        {!picking && (
          <>
            <p className="sub" style={{ marginTop: 8 }}>
              Missed a session? Pick it up on this day and the rest of the week slides along
              to match. Use the arrows above to land on the day you actually want to train.
            </p>
            <div className="btns" style={{ marginTop: 0 }}>
              <button disabled={!api.durable || candidates.length === 0}
                      onClick={() => setPicking(true)}>
                Move a missed session here
              </button>
              {hasOverrides && (
                <button disabled={!api.durable || busy} onClick={() => void resetWeek()}>
                  Reset week {week}
                </button>
              )}
            </div>
            {candidates.length === 0 && (
              <p className="sub" style={{ marginBottom: 0 }}>
                There are no earlier sessions to move — this is the first day of the plan.
              </p>
            )}
          </>
        )}

        {picking && chosen === null && (
          <>
            <p className="sub" style={{ marginTop: 8 }}>
              Which session did you miss? It will happen on{' '}
              <strong>{DOW_NAMES[dayIndex % 7]} {dateForDayIndex(startDate, dayIndex).slice(5)}</strong> instead.
            </p>
            {candidates.map((c) => (
              <button key={c.day} className="pickrow" onClick={() => setChosen(c.day)}>
                <span className="pickrow-day">{dayLabel(c.day)}</span>
                <span className="pickrow-sess">{LABELS[c.session]}</span>
                <span className="pickrow-go">›</span>
              </button>
            ))}
            <div className="btns"><button onClick={close}>Cancel</button></div>
          </>
        )}

        {picking && shift && (
          <>
            {shift.blocked ? (
              <Banner kind="bad">{shift.blocked}</Banner>
            ) : (
              <>
                <p className="sub" style={{ marginTop: 8 }}>
                  You missed <strong>{LABELS[shift.missedSession]}</strong>. The rest of week{' '}
                  {shift.week} slides along by {shift.shiftBy} day{shift.shiftBy === 1 ? '' : 's'}:
                </p>
                {shift.steps.map((s) => (
                  <div className="row" key={s.day}>
                    <span className="k">{dayLabel(s.day)}</span>
                    <span className="v">
                      <span className="dim">{LABELS[s.from]}</span> → <strong>{LABELS[s.to]}</strong>
                    </span>
                  </div>
                ))}
                {shift.warnings.map((w) => <Banner kind="warn" key={w}>{w}</Banner>)}
                <p className="sub">
                  Your eating moves with it. The post-training meal belongs to the session
                  rather than the day, so it follows Lift A, Lift B and the two longer runs
                  wherever they land. A day that becomes rest drops it, and a day that gains
                  a session gains it — about 200 kcal and 21g of protein either way.
                </p>
              </>
            )}
            <div className="btns">
              {!shift.blocked && (
                <button className="primary" disabled={!api.durable || busy}
                        onClick={() => void applyShift()}>
                  {busy ? 'Applying…' : 'Shift the week'}
                </button>
              )}
              <button disabled={busy} onClick={() => setChosen(null)}>Back</button>
              <button disabled={busy} onClick={close}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function countMoved(api: StoreApi, week: number): number {
  let n = 0;
  for (let d = (week - 1) * 7; d <= (week - 1) * 7 + 6; d++) if (isOverridden(api.state, d)) n++;
  return n;
}
